import { useEffect, useState, useLayoutEffect, useRef } from "react";
import { X, ArrowRight, ArrowLeft, Check } from "lucide-react";

/**
 * Lightweight guided tour.
 * Props:
 *  - steps: [{ selector: '[data-tour="key"]', title, body, placement? }]
 *  - storageKey: unique key to persist "seen" state per user/guild
 *  - open: forced-open bool (from parent)
 *  - onClose: () => void
 */
export default function Tour({ steps, storageKey, open, onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const retryRef = useRef(null);

  // Reset idx whenever the tour opens (fresh run)
  useEffect(() => { if (open) setIdx(0); }, [open]);

  // Find & track the target rect. Retries up to 20× (2 s) so the tour tolerates
  // nav items that are still loading when the tour tries to open.
  useLayoutEffect(() => {
    if (!open) return;
    const step = steps[idx];
    if (!step) return;

    let cancelled = false;
    let tries = 0;

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (!el) {
        tries += 1;
        if (tries < 20) {
          retryRef.current = setTimeout(measure, 100);
        } else {
          setRect(null);  // give up — tooltip shows centered
        }
        return;
      }
      try { el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" }); } catch {}
      const update = () => {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      };
      update();
      // post-scroll re-measure
      retryRef.current = setTimeout(update, 300);
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);
      // store listener cleanup
      Tour._cleanup = () => {
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
      };
    };

    measure();

    return () => {
      cancelled = true;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (Tour._cleanup) { Tour._cleanup(); Tour._cleanup = null; }
    };
  }, [open, idx, steps]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [open, idx]);

  if (!open) return null;
  const step = steps[idx];
  if (!step) return null;

  const finish = () => {
    if (storageKey) localStorage.setItem(storageKey, "1");
    setRect(null);
    onClose?.();
  };
  const next = () => (idx < steps.length - 1 ? setIdx(idx + 1) : finish());
  const prev = () => idx > 0 && setIdx(idx - 1);

  const tooltip = computeTooltipStyle(rect, step.placement);
  const spotlight = rect ? {
    top: rect.top - 8, left: rect.left - 8,
    width: rect.width + 16, height: rect.height + 16,
  } : null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="onboarding-tour">
      {/* Backdrop: dimmed layer that covers everything but the spotlight */}
      <div
        className="absolute inset-0 bg-black/75 pointer-events-auto"
        onClick={next}
        style={{
          // When there's a spotlight, cut it out with a radial-like mask using
          // a drop-shadow on an invisible overlay. The spotlight div itself
          // handles the glow.
          ...(spotlight ? {
            clipPath: `polygon(
              0 0, 100% 0, 100% 100%, 0 100%, 0 ${spotlight.top}px,
              ${spotlight.left}px ${spotlight.top}px,
              ${spotlight.left}px ${spotlight.top + spotlight.height}px,
              ${spotlight.left + spotlight.width}px ${spotlight.top + spotlight.height}px,
              ${spotlight.left + spotlight.width}px ${spotlight.top}px,
              0 ${spotlight.top}px
            )`,
          } : {}),
        }}
      />

      {/* Spotlight ring (pointer-events-auto to let the user click/advance) */}
      {spotlight && (
        <div
          className="absolute rounded-lg pointer-events-auto"
          style={{
            ...spotlight,
            transition: "top 220ms ease, left 220ms ease, width 220ms ease, height 220ms ease",
            boxShadow: "0 0 0 2px #8B5CF6, 0 0 24px 4px rgba(139,92,246,0.6)",
            cursor: "pointer",
          }}
          onClick={next}
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute pointer-events-auto bg-[#0D0E15] border border-[#8B5CF6]/40 rounded-lg shadow-2xl p-5 w-[340px] max-w-[90vw]"
        style={{ ...tooltip, animation: "fadeUp 0.25s ease forwards" }}
        data-testid="tour-tooltip"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] text-[#64748B] font-mono tracking-widest uppercase">
            Step {idx + 1} / {steps.length}
          </div>
          <button
            data-testid="tour-close"
            onClick={finish}
            className="text-[#94A3B8] hover:text-white transition-colors"
            aria-label="Skip tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="text-[17px] font-display font-bold text-white mb-1.5 leading-tight">{step.title}</h3>
        <p className="text-sm text-[#94A3B8] leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center gap-1 mb-4">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 rounded-full flex-1 ${i === idx ? "bg-[#8B5CF6]" : i < idx ? "bg-[#8B5CF6]/40" : "bg-white/10"}`} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            data-testid="tour-skip"
            onClick={finish}
            className="text-xs text-[#64748B] hover:text-white transition-colors"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button
                data-testid="tour-prev"
                onClick={prev}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#94A3B8] hover:text-white border border-white/10 hover:border-white/25 rounded-md transition-colors"
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            )}
            <button
              data-testid="tour-next"
              onClick={next}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-md transition-colors"
            >
              {idx === steps.length - 1 ? (
                <>Got it <Check className="w-3 h-3" /></>
              ) : (
                <>Next <ArrowRight className="w-3 h-3" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function computeTooltipStyle(rect, placement = "bottom") {
  const width = 340;
  const height = 240;
  const margin = 16;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) {
    return { top: `${(vh - height) / 2}px`, left: `${(vw - width) / 2}px` };
  }
  let top, left;
  const belowTop = rect.top + rect.height + margin;
  const aboveTop = rect.top - height - margin;
  if (placement === "top" || belowTop + height > vh - 8) {
    top = Math.max(8, aboveTop);
  } else {
    top = belowTop;
  }
  // Prefer right-of-target when the target is on the left edge (sidebar case)
  const rightOfTarget = rect.left + rect.width + margin;
  if (rect.left < 220 && rightOfTarget + width < vw - 8) {
    left = rightOfTarget;
    top = Math.max(8, Math.min(rect.top, vh - height - 8));
  } else {
    left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, vw - width - 8));
  }
  return { top: `${top}px`, left: `${left}px` };
}
